import React, { createContext, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useUser } from '../../contexts/UserContext';

// Tool context provides tool-specific information to child components
const ToolContext = createContext(null);

export function useToolContext() {
  const context = useContext(ToolContext);
  if (!context) {
    throw new Error('useToolContext must be used within a ToolWrapper');
  }
  return context;
}

/**
 * ToolWrapper — wrapper that provides tool-specific sidebar items and context
 * to child tool routes. Checks tool access before rendering.
 *
 * Props:
 *   toolSlug - unique identifier for the tool (e.g., 'drift-accumulation')
 *   toolName - display name for the tool (e.g., 'Drift & Accumulation')
 *   sidebarItems - array of sidebar nav items for this tool
 *   children - optional children (defaults to <Outlet />)
 */
function ToolWrapper({ toolSlug, toolName, sidebarItems = [], children }) {
  const { hasToolAccess, loading } = useUser();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!hasToolAccess(toolSlug)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, p: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <LockOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Access Denied
          </Typography>
          <Typography color="text.secondary">
            You do not have access to {toolName}. Please contact your administrator to request access.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const toolContextValue = {
    toolSlug,
    toolName,
    sidebarItems,
  };

  return (
    <ToolContext.Provider value={toolContextValue}>
      {children || <Outlet />}
    </ToolContext.Provider>
  );
}

export default ToolWrapper;
